/**
 * The three languages the site speaks, and the facts about each one that are
 * true regardless of which language you are currently reading.
 *
 * Nothing here is translated: an endonym is the language's own name for
 * itself, so «Русский» is spelled that way on the Uzbek page too. The names
 * of the languages *in* the current interface language live in the
 * dictionaries (`languageNames`), because those do change.
 */

export const locales = ['uz', 'ru', 'en'] as const;

export type Locale = (typeof locales)[number];

/** Uzbek is the site's own language — the fallback for anything ambiguous. */
export const defaultLocale: Locale = 'uz';

export function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (locales as readonly string[]).includes(value);
}

type LocaleMeta = {
  /** Two-letter code shown in the switcher — the selector's position label. */
  readonly code: string;
  /** The language's own name for itself. Never translated. */
  readonly endonym: string;
  /** `hreflang` / `<html lang>` value. */
  readonly hreflang: string;
  /** Open Graph `locale` — underscore form, region included. */
  readonly ogLocale: string;
  /** `Accept-Language` prefixes that should resolve to this locale. */
  readonly accepts: readonly string[];
};

export const localeMeta: Record<Locale, LocaleMeta> = {
  uz: {
    code: 'UZ',
    endonym: 'O‘zbekcha',
    hreflang: 'uz-UZ',
    ogLocale: 'uz_UZ',
    // `uz-Cyrl` too: a visitor asking for Cyrillic Uzbek is still an Uzbek
    // speaker, and Latin is the only script this site is set in.
    accepts: ['uz'],
  },
  ru: {
    code: 'RU',
    endonym: 'Русский',
    hreflang: 'ru-RU',
    ogLocale: 'ru_RU',
    // Russian is the regional lingua franca: readers arriving with a Central
    // Asian locale that this site does not speak are best served in Russian.
    accepts: ['ru', 'kk', 'ky', 'tg', 'be', 'uk'],
  },
  en: {
    code: 'EN',
    endonym: 'English',
    hreflang: 'en-US',
    ogLocale: 'en_US',
    accepts: ['en'],
  },
};

/** Cookie that remembers a deliberate choice, so `/` stops guessing. */
export const LOCALE_COOKIE = 'NEXT_LOCALE';

/** A year: the choice is a preference, not a session. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
